/**
 * One attention line (Customer 360, 01 §4.2; 02 §5.2): the single most serious
 * thing about the account — the source, a broken promise, old money past the
 * critical age — and, behind "Why this account (N)", every reason, in the same
 * words Today gives, then the page's own checks. Closed by default, and offered
 * only when it holds more than the line already says. When nothing is an
 * exception the line is quiet; when there is no reason at all there is no line.
 *
 * It is a row of the account's summary card, between the money and the work
 * (owner, 24 Oct 2026), not a banner of its own: the one place the page raises
 * its voice, and the only place a stale source is put into words.
 *
 * No score, nothing guessed: each reason is a fact on the account, checked
 * against the company's settings, which the list states.
 */
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconChevronDown, cx } from '@timely/ui';
import type { AttentionItem, AttentionTone } from '@timely/domain';
import { useFormatters } from '../../book/useFormatters';
import { useActiveWorkspace } from '../../workspace/WorkspaceProvider';
import { paths } from '../../workspace/navigation';
import { useAccount } from './accountPage';

/** Crimson for a failure or a broken commitment, amber for what is waiting, navy for an age (02 §19.2). */
const TONE_MARK: Record<AttentionTone, string> = { crimson: 'bg-signal-dang', amber: 'bg-signal-warn', navy: 'bg-ageing-3' };

const SOURCE_CODES = new Set(['source_attention', 'stale', 'balance_review']);

export function AttentionLine() {
    const page = useAccount();
    const { workspace } = useActiveWorkspace();
    const { money, count } = useFormatters();
    const id = useId();
    const [open, setOpen] = useState(false);
    const { attention, rights, row, statement } = page;
    const line = attention.line ?? attention.quiet;
    if (!line) return null;
    const exception = attention.line !== null;
    const more = attention.items.length > 1 || row.rank === 'Bad';
    const collection = statement.settings.collection;
    const thresholds = [
        `critical from ${count(collection.criticalOverdueDays)} days old`,
        `no contact counts after ${count(collection.staleContactDays)} days`,
        collection.highValueAmount !== null ? `high value from ${money.exact(collection.highValueAmount)}` : null,
        collection.promiseGraceDays > 0 ? `a promise is missed ${count(collection.promiseGraceDays)} days after its date` : null,
    ].filter(Boolean).join('; ');
    const detailsLink = rights.readSourceData && SOURCE_CODES.has(line.code)
        ? <Link to={paths(workspace.slug).setting('data')} className="font-semibold text-accent-text hover:underline whitespace-nowrap">See the details in Receivables data</Link>
        : null;

    return (
        <section aria-label={exception ? 'Needs attention' : 'Why this account'}
            className={cx('border-t', exception ? 'border-warn/25 bg-warn-bg' : 'border-separator bg-card')}>
            <div className="px-5 py-2 min-h-11 flex items-center gap-x-3 gap-y-1 flex-wrap">
                <span className={cx('text-secondary font-semibold flex-none', exception ? 'text-warn' : 'text-label-2')}>
                    {exception ? 'Needs attention' : 'Why this account'}
                </span>
                <span className="flex items-baseline gap-2 min-w-0 text-secondary text-label">
                    <Square item={line} />
                    <span>{line.text}</span>
                </span>
                {detailsLink && <span className="text-secondary">{detailsLink}</span>}
                {more && (
                    <button type="button" aria-expanded={open} aria-controls={`${id}-why`} onClick={() => setOpen(!open)}
                        className="ml-auto inline-flex items-center gap-1 min-h-9 text-secondary font-semibold text-accent-text hover:underline">
                        Why this account ({attention.items.length + (row.rank === 'Bad' ? 1 : 0)})
                        <span aria-hidden="true" className={cx('inline-flex transition-transform duration-150', open && 'rotate-180')}><IconChevronDown size={14} /></span>
                    </button>
                )}
            </div>
            {open && (
                <div id={`${id}-why`} className={cx('px-5 pt-1 pb-3 border-t', exception ? 'border-warn/15' : 'border-separator')}>
                    <ul aria-label="Reasons, most serious first" className="mt-2 space-y-1.5">
                        {row.rank === 'Bad' && (
                            <li className="flex items-baseline gap-2 text-secondary text-label">
                                <span aria-hidden="true" className="size-2 flex-none bg-signal-dang" />
                                Marked as bad debt: this account is recovery work, outside Today’s list.
                            </li>
                        )}
                        {attention.items.map(item => (
                            <li key={`${item.code}:${item.text}`} className="flex items-baseline gap-2 text-secondary text-label">
                                <Square item={item} />
                                <span>{item.text}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-3 text-caption text-label-3 max-w-3xl">
                        Each reason is a fact on this account, checked against your company’s collection settings: {thresholds}.
                        Critical age is an age, not a due date. There is no score, and nothing here is a prediction.
                    </p>
                </div>
            )}
        </section>
    );
}

/**
 * The line in short, for the strip above Bills, Contacts and the full activity where there is no rail: the one fact,
 * only when it is an exception, on the same tint. Why, and every other reason, stay on Overview.
 */
export function AttentionBrief() {
    const { attention } = useAccount();
    const line = attention.line;
    if (!line) return null;
    return (
        <p className="border-t border-warn/25 bg-warn-bg px-4 py-2 flex items-baseline gap-2 text-secondary text-label">
            <span className="sr-only">Needs attention: </span>
            <Square item={line} />
            <span className="min-w-0">{line.text}</span>
        </p>
    );
}

/** The item's colour as a square, and its weight in words for the ear (never the colour alone). */
function Square({ item }: { item: AttentionItem }) {
    return (
        <>
            <span aria-hidden="true" className={cx('size-2 flex-none translate-y-[-1px]', TONE_MARK[item.tone])} />
            <span className="sr-only">{item.tone === 'crimson' ? 'Serious: ' : item.tone === 'amber' ? 'Waiting: ' : 'Age: '}</span>
        </>
    );
}
